import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Lock, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const inputCls =
  "w-full bg-[#F7F7F2] border border-[#E4E4DE] px-4 py-3 text-sm text-zinc-900 placeholder-zinc-600 focus:border-[#C8FF1E] focus:outline-none transition-colors";

export const CheckoutModal = () => {
  const { items, subtotal, checkoutOpen, setCheckoutOpen, clearCart } = useCart();
  const [step, setStep] = useState(1);
  const [placing, setPlacing] = useState(false);
  const [order, setOrder] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", address: "", city: "", zip: "", country: "" });
  const [card, setCard] = useState({ number: "", expiry: "", cvc: "" });

  const shipping = subtotal >= 150 ? 0 : 9;
  const total = subtotal + shipping;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setCardField = (k) => (e) => setCard({ ...card, [k]: e.target.value });

  const step1Valid = Object.values(form).every((v) => v.trim().length > 1);
  const step2Valid = card.number.trim().length >= 12 && card.expiry.trim().length >= 4 && card.cvc.trim().length >= 3;

  const close = () => {
    setCheckoutOpen(false);
    setTimeout(() => {
      setStep(1);
      setOrder(null);
    }, 300);
  };

  const placeOrder = async () => {
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.productId, size: i.size, qty: i.qty })),
        customer: form,
      });
      setOrder(data);
      clearCart();
      setStep(3);
      toast.success("Order placed — confirmation sent");
    } catch (e) {
      toast.error("Could not place order. Please try again.");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <AnimatePresence>
      {checkoutOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
          <motion.div
            data-testid="checkout-modal"
            className="pointer-events-auto w-full max-w-lg max-h-[90vh] overflow-y-auto bg-[#FFFFFF] border border-[#E4E4DE]"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center justify-between border-b border-[#E4E4DE] px-6 py-4">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">
                {step === 3 ? "Order confirmed" : `Checkout — step ${step} of 2`}
              </p>
              <button
                data-testid="checkout-close-btn"
                onClick={close}
                className="h-9 w-9 flex items-center justify-center border border-[#E4E4DE] text-zinc-500 hover:text-[#4D7C0F] hover:border-[#C8FF1E] transition-colors"
                aria-label="Close checkout"
              >
                <X size={16} />
              </button>
            </div>

            {step === 1 && (
              <div className="px-6 py-6 space-y-4">
                <h3 className="font-display text-2xl font-bold uppercase tracking-tight">Shipping details</h3>
                <input data-testid="checkout-name-input" className={inputCls} placeholder="Full name" value={form.name} onChange={set("name")} />
                <input data-testid="checkout-email-input" className={inputCls} type="email" placeholder="Email address" value={form.email} onChange={set("email")} />
                <input data-testid="checkout-address-input" className={inputCls} placeholder="Street address" value={form.address} onChange={set("address")} />
                <div className="grid grid-cols-2 gap-4">
                  <input data-testid="checkout-city-input" className={inputCls} placeholder="City" value={form.city} onChange={set("city")} />
                  <input data-testid="checkout-zip-input" className={inputCls} placeholder="ZIP / Postcode" value={form.zip} onChange={set("zip")} />
                </div>
                <input data-testid="checkout-country-input" className={inputCls} placeholder="Country" value={form.country} onChange={set("country")} />
                <button
                  data-testid="checkout-continue-btn"
                  disabled={!step1Valid}
                  onClick={() => setStep(2)}
                  className="w-full bg-[#C8FF1E] py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black hover:bg-[#B0E600] disabled:opacity-40 transition-colors"
                >
                  Continue to payment
                </button>
              </div>
            )}

            {step === 2 && (
              <div className="px-6 py-6 space-y-4">
                <h3 className="font-display text-2xl font-bold uppercase tracking-tight">Payment</h3>
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  Demo checkout — no real charge is made
                </p>
                <input data-testid="checkout-card-number-input" className={inputCls} placeholder="Card number" value={card.number} onChange={setCardField("number")} />
                <div className="grid grid-cols-2 gap-4">
                  <input data-testid="checkout-card-expiry-input" className={inputCls} placeholder="MM / YY" value={card.expiry} onChange={setCardField("expiry")} />
                  <input data-testid="checkout-card-cvc-input" className={inputCls} placeholder="CVC" value={card.cvc} onChange={setCardField("cvc")} />
                </div>
                <div className="border border-[#E4E4DE] px-4 py-3 space-y-1.5 font-mono text-xs">
                  <div className="flex justify-between text-zinc-500">
                    <span>Subtotal</span>
                    <span data-testid="checkout-subtotal">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-zinc-500">
                    <span>Shipping</span>
                    <span data-testid="checkout-shipping">{shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}</span>
                  </div>
                  <div className="flex justify-between border-t border-[#E4E4DE] pt-1.5 text-sm font-bold text-zinc-900">
                    <span>Total</span>
                    <span data-testid="checkout-total" className="text-[#4D7C0F]">${total.toFixed(2)}</span>
                  </div>
                </div>
                <button
                  data-testid="checkout-submit-btn"
                  disabled={!step2Valid || placing}
                  onClick={placeOrder}
                  className="w-full flex items-center justify-center gap-2 bg-[#C8FF1E] py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black hover:bg-[#B0E600] disabled:opacity-40 transition-colors"
                >
                  <Lock size={14} /> {placing ? "Placing order…" : `Pay $${total.toFixed(2)}`}
                </button>
                <button
                  data-testid="checkout-back-btn"
                  onClick={() => setStep(1)}
                  className="w-full py-2 font-mono text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-700 transition-colors"
                >
                  Back to shipping
                </button>
              </div>
            )}

            {step === 3 && order && (
              <div data-testid="order-confirmation" className="px-6 py-10 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, damping: 14 }}
                  className="mx-auto flex h-16 w-16 items-center justify-center border border-[#C8FF1E]"
                >
                  <CheckCircle2 size={30} className="text-[#4D7C0F]" />
                </motion.div>
                <h3 className="mt-6 font-display text-3xl font-extrabold uppercase tracking-tighter">
                  Order locked in.
                </h3>
                <p className="mt-3 font-mono text-xs uppercase tracking-widest text-zinc-500">
                  Order <span data-testid="order-number" className="text-[#4D7C0F]">{order.order_no}</span>
                </p>
                <p className="mt-1 font-mono text-xs uppercase tracking-widest text-zinc-500">
                  Total charged: ${order.total.toFixed(2)} — ships within 48h
                </p>
                <p data-testid="order-email-note" className="mt-1 font-mono text-xs uppercase tracking-widest text-[#4D7C0F]">
                  A receipt was emailed to {order.customer.email}
                </p>
                <button
                  data-testid="checkout-done-btn"
                  onClick={close}
                  className="mt-8 bg-[#C8FF1E] px-10 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black hover:bg-[#B0E600] transition-colors"
                >
                  Keep browsing
                </button>
              </div>
            )}
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};
